<template>
  <vue-final-modal
    :value="visible"
    name="login-dialog"
    :classes="['login-dialog__container']"
    :content-class="['login-dialog__content', 'v--modal-box', 'v--modal']"
    :click-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
  >
    <div
      class="v--modal-close-top"
      v-loading.fullscreen.lock="fullscreenLoading"
    >
      <el-button
        @click="closeLoginDialog"
        icon="el-icon-circle-close"
        style="font-size: 2rem; position: relative; right: 10px"
        type="text"
      ></el-button>
    </div>
    <div style="display: inline-block; min-width: 100%; text-align: center">
      <div class="text-center button-download-wrapper">
        <LoginForms />
      </div>
    </div>
  </vue-final-modal>
</template>

<script setup lang="ts">
/**
 * 共通ログインダイアログ（vue-final-modal）。
 * open / close / success / context / auth_state_changed は mitt 購読
 * （未移行 hub 発火は emitter の hub→mitt forward。firebase-lib の発火は当面 hub のまま）。
 */
import { ref, onBeforeMount, onBeforeUnmount } from 'vue'
import { useEventListener } from '@vueuse/core'
import {
  AUTH_STATE_CHANGED,
  OPEN_LOGIN_DIALOG,
  emitLoginDialogClosePair,
  emitLoginSuccess,
  emitSetLoginContext,
  emitter
} from '@/plugins/emitter'
import LoginForms from '@/components/auth/LoginSpecialForms.vue'

const visible = ref(false)
const fullscreenLoading = ref(false)
const isProcessingEvent = ref(false)

function emitClosed() {
  emitLoginDialogClosePair()
}

function openLoginDialog(context?: unknown) {
  console.log('[LoginDialog openLoginDialog] コンテキスト:', context)

  if (isProcessingEvent.value) {
    console.log(
      '[LoginDialog openLoginDialog] 既に処理中のため、スキップします'
    )
    return
  }

  isProcessingEvent.value = true

  if (context) {
    emitSetLoginContext(context)
  }

  visible.value = true

  setTimeout(() => {
    isProcessingEvent.value = false
  }, 100)
}

function closeLoginDialog() {
  if (!visible.value) return
  visible.value = false
  emitClosed()
}

/** 背景クリック等で VFM が閉じたとき。closeLoginDialog 経由で既に false なら二重 emit しない */
function onVisibleInput(value: boolean) {
  if (value) {
    visible.value = true
    return
  }
  if (!visible.value) return
  visible.value = false
  emitClosed()
}

/**
 * ログイン成功: 旧 vue-js-modal hide と同様、closed を先に出してから login-success。
 * （Payment は closed、完了画面パネルは closed 時点の認証状態で abandon 判定）
 */
function handleAuthStateChanged(user: unknown) {
  console.log('handleAuthStateChanged')
  if (user) {
    console.log('login-success')
    if (visible.value) {
      visible.value = false
      emitClosed()
    }
    emitLoginSuccess()
  }
}

function onOpenLoginDialog(context?: unknown) {
  openLoginDialog(context)
}

useEventListener(window, 'popstate', closeLoginDialog)

onBeforeMount(() => {
  emitter.on(OPEN_LOGIN_DIALOG, onOpenLoginDialog)
  emitter.on(AUTH_STATE_CHANGED, handleAuthStateChanged)
})

onBeforeUnmount(() => {
  emitter.off(OPEN_LOGIN_DIALOG, onOpenLoginDialog)
  emitter.off(AUTH_STATE_CHANGED, handleAuthStateChanged)
})
</script>

<style lang="scss" scoped>
.text-center {
  text-align: center;
}

.v--modal-close-top {
  position: absolute;
  right: 0;
  top: 0;
}

// NOTE: LoginSpecialForms.vue の scoped style と同じセレクタ構造・同一詳細度になるため、
// !important なしだと本番ビルドの CSS 結合順序次第でこちらの上書きが負けて緑背景が出ることがある。
.button-download-wrapper {
  .button-download-pdf {
    padding: 0 !important;
    background: none !important;

    .login {
      padding: 0 !important;
      width: 100% !important;
    }
  }
}
</style>

<!-- content-class / container は vue-final-modal 内部ノードのため scoped 外 -->
<style lang="scss">
.login-dialog__container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 1001;
}

.login-dialog__content.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  flex: 0 0 auto;
  width: calc(100% - 32px);
  max-width: 500px;
  margin-block: auto;
  border-radius: 16px;
  padding: 20px;
  background: #fff;
}

@media screen and (width <= 640px) {
  .login-dialog__content.v--modal-box.v--modal {
    padding: 24px;
  }
}
</style>
